<template>
  <div class="recruitment">
    <div class="container">
      <h1 class="recruitment__title">Outstaffing &amp; Recruitment</h1>

      <nav class="recruitment__tabs" aria-label="Recruitment sections">
        <router-link
          v-for="t in tabs"
          :key="t.id"
          :to="'/recruitment/' + t.id"
          class="recruitment__tab"
          :class="{ 'is-active': tab === t.id }"
        >{{ t.label }}</router-link>
      </nav>

      <p class="recruitment__intro">{{ current.intro }}</p>

      <form class="recruitment__search" role="search" @submit.prevent="runSearch">
        <svg class="recruitment__search-icon" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" stroke-width="1.8" />
          <path d="M12.5 12.5 16 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
        <input
          v-model.trim="query"
          type="search"
          class="recruitment__search-input"
          :placeholder="current.placeholder"
          autocomplete="off"
          @input="onQueryInput"
        />
        <button v-if="query" type="button" class="recruitment__search-clear" aria-label="Clear search" @click="clearSearch">×</button>
      </form>

      <p v-if="statusText" class="recruitment__status">{{ statusText }}</p>

      <div v-if="visible.length" class="cand-grid">
        <article
          v-for="c in visible"
          :key="c.id"
          class="cand-card"
          tabindex="0"
          role="button"
          @click="openCandidate(c)"
          @keydown.enter.prevent="openCandidate(c)"
          @keydown.space.prevent="openCandidate(c)"
        >
          <h3 class="cand-card__stack">{{ c.stack }}</h3>
          <p class="cand-card__name">{{ displayName(c) }}</p>
          <p class="cand-card__where">
            <span>{{ c.location }}</span>
            <span v-if="c.timezone" class="cand-card__tz">{{ c.timezone }}</span>
          </p>
          <p v-if="c.seniority || c.experienceYears != null" class="cand-card__meta">{{ metaLine(c) }}</p>
          <p class="cand-card__rate">{{ rateLabel(c) }}</p>
        </article>
      </div>

      <p v-else-if="!loading && !statusText" class="recruitment__status">
        No profiles in this section yet.
      </p>
    </div>

    <!-- Candidate profile: section order follows a WDevs resume, and every
         block renders only when the candidate actually has that data. -->
    <transition name="cand-fade">
      <div v-if="active" class="cand-modal" role="dialog" aria-modal="true" @click.self="closeCandidate">
        <div class="cand-modal__panel">
          <CvDocument
            :active="active"
            :share-url="profileUrl(active)"
            closable
            @close="closeCandidate"
            @request="showRequest = true"
          />
        </div>
      </div>
    </transition>

    <ModalContact
      v-if="showRequest"
      :startProjectModal="true"
      :hideProjectScope="true"
      :formMessage="requestMessage"
      :formUrl="requestUrl"
      title="Request a candidate"
      @close="showRequest = false"
    />
  </div>
</template>

<script>
import ModalContact from "@/components/ModalContact.vue";
import CvDocument from "@/components/recruitment/CvDocument.vue";

const TABS = [
  {
    id: "outstaffing",
    label: "Outstaffing",
    category: "Outstaffing",
    intro:
      "Hire a vetted engineer who joins your team and works to your process, your board and your standards — you keep the management, we carry the contract, payroll and replacement risk. The profiles below are available now; search by stack, seniority, time zone or anything else that matters to you.",
    placeholder: "Search by stack, seniority, location…",
  },
  {
    id: "recruitment",
    label: "Recruitment",
    category: "Recruitment",
    intro:
      "When the hire belongs on your own payroll, we run the search: sourcing, screening and technical validation, so you interview only people who already passed it. Search the current pool below.",
    placeholder: "Search by stack, seniority, location…",
  },
];

export default {
  name: "Recruitment",
  components: { ModalContact, CvDocument },
  data() {
    return {
      tabs: TABS,
      candidates: [],
      loading: false,
      loadError: "",
      query: "",
      searchIds: null, // ordered ids from the semantic search; null = no query
      searching: false,
      searchError: "",
      searchTimer: null,
      showRequest: false,
      viewId: null,
      viewStartedAt: 0,
    };
  },
  computed: {
    tab() {
      const t = this.$route.params.tab;
      return TABS.some((x) => x.id === t) ? t : "outstaffing";
    },
    current() {
      return TABS.find((t) => t.id === this.tab) || TABS[0];
    },
    active() {
      const id = this.$route.params.id;
      return id ? this.candidates.find((c) => c.id === id) || null : null;
    },
    // Cards to render: the search order while a query is active, otherwise all.
    visible() {
      if (!this.searchIds) return this.candidates;
      const byId = new Map(this.candidates.map((c) => [c.id, c]));
      return this.searchIds.map((id) => byId.get(id)).filter(Boolean);
    },
    statusText() {
      if (this.loading) return "Loading profiles…";
      if (this.loadError) return this.loadError;
      if (this.searching) return "Searching…";
      if (this.searchError) return this.searchError;
      if (this.searchIds && !this.visible.length) return "No profiles match this search.";
      return "";
    },
    requestMessage() {
      if (!this.active) return "";
      return "Request for candidate " + this.displayName(this.active) +
        " — " + this.active.stack + ".";
    },
    /* Sent as its own field: the profiles are anonymised, so the link is the
       only thing in the email that says which person is meant. */
    requestUrl() {
      return this.active ? this.profileUrl(this.active) : "";
    },
  },
  watch: {
    // The open profile decides which Google font the document needs, and one
    // opening is one view: the previous one closes as this one starts.
    active(c, prev) {
      if (prev) this.endView();
      if (c) this.startView(c);
    },
    // Switching tab reloads the pool and drops the previous query.
    tab() {
      this.clearSearch();
      this.loadCandidates();
    },
  },
  mounted() {
    this.loadCandidates();
    document.addEventListener("keydown", this.onKeydown);
    // Closing the tab is the common way to end a view.
    window.addEventListener("beforeunload", this.endView);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeunload", this.endView);
    clearTimeout(this.searchTimer);
    this.endView();
  },
  methods: {
    displayName(c) {
      const initial = c.surname ? c.surname.trim().charAt(0).toUpperCase() + "." : "";
      return [c.name, initial].filter(Boolean).join(" ");
    },
    metaLine(c) {
      const years = c.experienceYears != null ? c.experienceYears + " yrs" : "";
      return [c.seniority, years].filter(Boolean).join(" · ");
    },
    rateLabel(c) {
      if (!c.rate) return "Rate on request";
      const cur = !c.currency || c.currency === "USD" ? "$" : c.currency + " ";
      return cur + c.rate + "/hour";
    },
    cvParagraphs(c) {
      return String(c.cv || "").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
    },
    /**
     * Pull a Google font in on demand. Each family is requested once per page
     * load; a family that fails to load simply falls back to the site font.
     */
    /**
     * Tell the server a profile was opened. The id comes back so the time spent
     * can be attached when the visitor leaves; a failure here is silent, because
     * analytics must never break the page.
     */
    async startView(c) {
      this.viewId = null;
      this.viewStartedAt = Date.now();
      try {
        const res = await fetch("/api/wdevs/views", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // The card is addressed by person, but the view belongs to the
          // version actually on display.
          body: JSON.stringify({ candidateId: c.versionId || c.id }),
        });
        const data = await res.json();
        this.viewId = data.viewId || null;
      } catch (e) { /* tracking is best effort */ }
    },
    /** Sent with sendBeacon so it survives the page going away. */
    endView() {
      if (!this.viewId || !this.viewStartedAt) return;
      const seconds = Math.round((Date.now() - this.viewStartedAt) / 1000);
      const body = JSON.stringify({ viewId: this.viewId, seconds });
      this.viewId = null;
      this.viewStartedAt = 0;
      if (!seconds) return;
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon("/api/wdevs/views/duration", new Blob([body], { type: "application/json" }));
        } else {
          fetch("/api/wdevs/views/duration", { method: "POST", body, keepalive: true });
        }
      } catch (e) { /* nothing else to try */ }
    },
    profileUrl(c) {
      const origin = typeof window !== "undefined" ? window.location.origin : "https://wdevs.com";
      return origin + "/recruitment/" + c.section + "/" + c.id;
    },
    async loadCandidates() {
      this.loading = true;
      this.loadError = "";
      try {
        const res = await fetch("/api/wdevs/candidates?section=" + encodeURIComponent(this.tab));
        if (!res.ok) throw new Error("HTTP " + res.status);
        const data = await res.json();
        this.candidates = Array.isArray(data.candidates) ? data.candidates : [];
      } catch (e) {
        this.candidates = [];
        this.loadError = "Profiles are unavailable right now.";
      } finally {
        this.loading = false;
      }
    },
    onQueryInput() {
      clearTimeout(this.searchTimer);
      this.searchError = "";
      if (!this.query) {
        this.searchIds = null;
        this.searching = false;
        return;
      }
      this.searching = true;
      this.searchTimer = setTimeout(this.runSearch, 280);
    },
    /**
     * Reuses the site-wide semantic search, hard-scoped to this tab's category,
     * then maps the returned profile URLs back onto the loaded cards.
     */
    async runSearch() {
      const q = this.query;
      if (!q) return;
      clearTimeout(this.searchTimer);
      this.searching = true;
      try {
        const res = await fetch("/api/wdevs/search", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: q, topK: 25, categories: [this.current.category] }),
        });
        if (!res.ok) throw new Error("HTTP " + res.status);
        const data = await res.json();
        if (q !== this.query) return; // a newer query superseded this one
        const ids = (data.results || [])
          .map((r) => String(r.url || "").split("/").filter(Boolean).pop())
          .filter(Boolean);
        this.searchIds = [...new Set(ids)];
        this.searchError = "";
      } catch (e) {
        if (q !== this.query) return;
        this.searchIds = null;
        this.searchError = "Search is unavailable right now — showing every profile.";
      } finally {
        if (q === this.query) this.searching = false;
      }
    },
    clearSearch() {
      clearTimeout(this.searchTimer);
      this.query = "";
      this.searchIds = null;
      this.searching = false;
      this.searchError = "";
    },
    openCandidate(c) {
      this.shared = false;
      this.$router.push("/recruitment/" + this.tab + "/" + c.id);
    },
    closeCandidate() {
      this.shared = false;
      this.$router.push("/recruitment/" + this.tab);
    },
    onKeydown(e) {
      if (e.key === "Escape" && this.active) this.closeCandidate();
    },
  },
};
</script>

<style lang="scss">
@import "../scss/_variables.scss";
@import "include-media/dist/_include-media.scss";

.recruitment {
  padding: 40px 0 80px;
  @include media(">=tablet") { padding: 60px 0 100px; }

  &__title {
    font-size: 32px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: $black;
    margin-bottom: 20px;
    @include media(">=tablet") { font-size: 44px; }
  }

  &__tabs {
    display: flex;
    border-bottom: 1px solid rgba($black, 0.1);
    margin-bottom: 24px;
  }

  &__tab {
    position: relative;
    padding: 10px 0;
    margin-right: 32px;
    font-size: 16px;
    font-weight: 700;
    color: rgba($black, 0.55);
    text-decoration: none;
    transition: color 0.2s ease;

    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: -1px;
      width: 100%;
      height: 2px;
      background: $orange;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
    }
    &:hover { color: $black; }
    &.is-active { color: $orange; }
    &.is-active::after { transform: scaleX(1); }
  }

  &__intro {
    max-width: 820px;
    font-size: 17px;
    line-height: 1.7;
    color: rgba($black, 0.78);
    margin-bottom: 28px;
  }

  &__search {
    position: relative;
    display: flex;
    align-items: center;
    max-width: 640px;
    margin-bottom: 28px;
  }
  &__search-icon { position: absolute; left: 18px; color: $grey; pointer-events: none; }
  &__search-input {
    width: 100%;
    padding: 15px 46px;
    border: 1px solid rgba($black, 0.14);
    border-radius: 999px;
    background: $gray;
    font-family: $primary-fonts;
    font-size: 16px;
    color: $black;
    transition: border-color 0.25s ease, box-shadow 0.25s ease, background-color 0.25s ease;

    &::placeholder { color: $grey; }
    &:focus {
      outline: none;
      background: $white;
      border-color: rgba($orange, 0.6);
      box-shadow: 0 0 0 4px rgba($orange, 0.12);
    }
    &::-webkit-search-cancel-button { -webkit-appearance: none; appearance: none; }
  }
  &__search-clear {
    position: absolute;
    right: 14px;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 50%;
    background: rgba($black, 0.08);
    color: $black;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    transition: background-color 0.2s ease;
    &:hover { background: rgba($orange, 0.2); }
  }

  &__status { color: $grey; font-size: 16px; padding: 8px 0 24px; }
}

// ── cards ──────────────────────────────────────────────────────────────────
.cand-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @include media(">=phone") { grid-template-columns: repeat(2, 1fr); }
  @include media(">=tablet-xl") { grid-template-columns: repeat(3, 1fr); }
  @include media(">=widescreen") { grid-template-columns: repeat(4, 1fr); }
}

.cand-card {
  padding: 24px;
  border: 1px solid rgba($black, 0.07);
  border-radius: 16px;
  background: $white;
  cursor: pointer;
  // Layered, warm-tinted shadow rather than a flat grey one.
  box-shadow: 0 1px 2px rgba($black, 0.04), 0 10px 24px -18px rgba($black, 0.4);
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover,
  &:focus-visible {
    transform: translateY(-3px);
    border-color: rgba($orange, 0.35);
    box-shadow: 0 2px 4px rgba($black, 0.05), 0 18px 36px -20px rgba($orange, 0.55);
  }
  &:focus-visible { outline: 2px solid $orange; outline-offset: 3px; }

  &__stack {
    font-size: 26px;
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: $black;
    margin-bottom: 10px;
  }
  &__name { font-size: 15px; font-weight: 600; color: rgba($black, 0.72); margin-bottom: 12px; }
  &__where {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    font-size: 14px;
    color: $grey;
    margin-bottom: 6px;
  }
  &__tz {
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 999px;
    background: rgba($black, 0.06);
    font-size: 12px;
    font-weight: 600;
    color: rgba($black, 0.6);
  }
  &__meta { font-size: 14px; color: $grey; margin-bottom: 14px; }
  &__rate { font-size: 18px; font-weight: 800; color: $orange; }
}

// The profile popup is only the overlay now: the document inside it lives in
// CvDocument.vue, which the shared /cv/<token> page renders as well.
.cand-modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  background: rgba($black, 0.6);

  &__panel {
    display: flex;
    width: 100%;
    max-width: 880px;
    max-height: calc(100vh - 48px);
    margin: auto;
  }
}

.cand-fade-enter-active,
.cand-fade-leave-active { transition: opacity 0.22s ease; }
.cand-fade-enter-from,
.cand-fade-leave-to { opacity: 0; }
</style>
